<script lang="ts">
	import { get } from 'svelte/store';
	import { push } from 'svelte-spa-router';
	import { format } from 'svelte-i18n';
	import { location } from 'svelte-spa-router';
	import { scenes } from 'src/stores/lists/scenes';
	import { edit, initialEdit } from 'src/stores/ui/edit_lists';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { isFlow, requestDeleteScene } from '../list-helpers';
	import FlowEditor from '../../Flows/FlowEditor.svelte';
	import AddEditButtons from '../../AddEditButtons.svelte';
	import {
		Container,
		CardGrid,
		ListMenuCard,
		Toolbar,
		Breadcrumbs,
		BreadcrumbsFlexWrapper,
		Button,
		Text,
		Stack,
		Modal,
		toast,
		ActionsContainer,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import ControlsScene from '../Scenes/ControlsScene.svelte';
	import type { ISceneObj } from '../../../types/SceneType';
	import { resetDrawer } from '../../../helpers/drawer_helpers';
	import { mobile } from 'src/services/mobile-rework';
	import FlowSharingModal from '../../Flows/modals/FlowSharingModal.svelte';
	import { setPendingFlowImport } from '../../Flows/sharing/importHandoff';
	import type { FlowImportMapping, PortableFlowPackage } from '../../Flows/sharing/types';
	import FlowNavigatorSidebar from '../../Flows/FlowNavigatorSidebar.svelte';
	import { flowEditorState } from 'src/stores/flow_editor';
	import { user_data } from 'src/stores/account/user';
	import {
		type FlowAccountTier,
		getFlowTierLimits,
		getFlowUsageWithWorkspace,
		limitReached,
	} from '../../Flows/core/config/flow-entitlements';

	export let flowId: string = '';
	export let basePath = '/home/flows';
	/** Filtered flows from HomeListsController (room + search); falls back to store if unset */
	export let list: ISceneObj[] | undefined = undefined;

	function resolveEditorFlowId(
		inEditor: boolean,
		flowsIndex: number,
		parts: string[],
		propFlowId: string,
	): string {
		if (inEditor && flowsIndex !== -1) {
			return parts[flowsIndex + 1] ?? '';
		}
		if (propFlowId && propFlowId !== '-1') {
			return propFlowId;
		}
		return '';
	}

	let flowsList: ISceneObj[] = [];
	let allFlowsList: ISceneObj[] = [];
	let showDeleteModal = false;
	let flowToDelete: ISceneObj | null = null;
	let showImportModal = false;
	let flowEditorRef: { requestNavigation?: (targetPath: string) => void } | undefined;
	let accountTier: FlowAccountTier = 'free';

	// Reactive to route: derive editor state from path so switching flows updates correctly
	$: pathParts = ($location || '').split('/').filter(Boolean);
	$: flowsIdx = pathParts.indexOf('flows');
	// List route is /home/flows/-1; editor routes are /home/flows/:id/edit (incl. new)
	$: inEditorPath = flowsIdx !== -1 && pathParts[flowsIdx + 2] === 'edit';
	$: isDefaultWorkspace =
		flowsIdx !== -1 && pathParts[flowsIdx + 1] === '-1' && !pathParts[flowsIdx + 2];
	$: editorFlowId = isDefaultWorkspace
		? 'new'
		: resolveEditorFlowId(inEditorPath, flowsIdx, pathParts, flowId);
	$: showEditor = inEditorPath || isDefaultWorkspace;
	$: isMobile = mobile.isMobile;

	$: FLOWS_BASE = basePath;

	/** ListMenuCard accepts DS tokens; scene meta may store legacy color names */
	function listMenuCardBackground(meta: ISceneObj['_meta'] | undefined) {
		return meta?.backgroundColor as any;
	}

	$: {
		const fromParent = Array.isArray(list) ? list : undefined;
		allFlowsList = (Object.values($scenes) as ISceneObj[])
			.filter((s) => isFlow(s))
			.sort((a, b) => (a._meta?.position || 0) - (b._meta?.position || 0));
		flowsList = (fromParent ?? allFlowsList)
			.slice()
			.sort((a, b) => (a._meta?.position || 0) - (b._meta?.position || 0));
	}
	$: accountTier = $user_data?.premium ? 'premium' : 'free';
	$: flowLimits = getFlowTierLimits(Boolean($user_data?.premium));
	$: flowUsage = getFlowUsageWithWorkspace(
		allFlowsList,
		editorFlowId,
		showEditor ? $flowEditorState.nodes : [],
		showEditor ? $flowEditorState.edges : [],
	);
	$: createFlowDisabled = limitReached(allFlowsList.length, flowLimits.flows);

	function openFlowEditor(sceneId: string) {
		if ($edit) {
			edit.set(false);
			initialEdit.set(false);
		}
		push(`${FLOWS_BASE}/${sceneId}/edit`);
	}

	function createNewFlow() {
		if (createFlowDisabled) {
			toast.open({ type: 'warning', title: `Flow limit reached (${flowLimits.flows})` });
			return;
		}
		push(`${FLOWS_BASE}/new/edit`);
	}

	function requestEditorNavigation(targetPath: string) {
		if (flowEditorRef?.requestNavigation) {
			flowEditorRef.requestNavigation(targetPath);
			return;
		}
		push(targetPath);
	}

	function switchEditorFlow(sceneId: string) {
		if (sceneId === editorFlowId) return;
		requestEditorNavigation(`${FLOWS_BASE}/${sceneId}/edit`);
	}

	function handleFlowImport(
		event: CustomEvent<{ flow: PortableFlowPackage; mapping: FlowImportMapping }>,
	) {
		setPendingFlowImport(event.detail);
		showImportModal = false;
		push(`${FLOWS_BASE}/new/edit`);
	}

	function onCardClick(flow: ISceneObj) {
		if ($edit) return;
		setSidePanelState({ type: 'flow', id: String(flow._id || flow.id), open: true });
	}

	function onCardEdit(event: MouseEvent, flow: ISceneObj) {
		event.stopPropagation();
		openFlowEditor(String(flow._id || flow.id));
	}

	function openDeleteModal(event: MouseEvent, flow: ISceneObj) {
		event.stopPropagation();
		flowToDelete = flow;
		showDeleteModal = true;
	}

	function closeDeleteModal() {
		showDeleteModal = false;
		flowToDelete = null;
	}

	async function confirmDeleteFlow() {
		if (!flowToDelete) return;
		const flowId = String(flowToDelete._id || flowToDelete.id);
		const res = await requestDeleteScene(flowId);
		if (res.isok) {
			scenes.remove(flowId);
			resetDrawer();
			closeDeleteModal();
		} else {
			toast.open({
				type: 'danger',
				title: get(format)('flow_delete_failed', { default: 'Failed to delete flow' }),
			});
		}
	}
</script>

{#if showEditor}
	<div class="flow-editor-route">
		<FlowNavigatorSidebar
			flows={allFlowsList}
			activeFlowId={editorFlowId}
			showDraft={inEditorPath || $flowEditorState.isDirty}
			onSelect={switchEditorFlow}
			{flowUsage}
			{flowLimits}
			{accountTier}
			createDisabled={createFlowDisabled}
			onCreate={() => {
				if (createFlowDisabled) {
					toast.open({ type: 'warning', title: `Flow limit reached (${flowLimits.flows})` });
					return;
				}
				requestEditorNavigation(`${FLOWS_BASE}/new/edit`);
			}}
		/>
		<div class="flow-editor-main">
			<FlowEditor bind:this={flowEditorRef} flowId={editorFlowId} />
		</div>
	</div>
{:else}
	<Container padding="scrollPaddings">
		<Stack gap="m" class="d-flex flex-direction-column">
			<Toolbar class="mb-m" isSticky useNewLayout={$showNewLayout} hideSlotRightSide={true}>
				<svelte:fragment slot="breadcrumbs">
					<BreadcrumbsFlexWrapper gap="s">
						<div class="flex-1 min-width-0" slot="left">
							<Breadcrumbs
								class="flex-1 min-width-0"
								listClasses="width-full"
								aria-label={$format('label_flows', { default: 'Flows' })}
								links={[{ label: $format('label_flows', { default: 'Flows' }) }]}
							/>
						</div>
						<div slot="right">
							<Button
								variant="secondary"
								icon="fas fa-file-import"
								on:click={() => (showImportModal = true)}
							>
								Import
							</Button>
							<AddEditButtons
								disableEditButton={flowsList.length <= 0 || isMobile}
								disableAddButton={createFlowDisabled}
								on:add_click={createNewFlow}
							/>
						</div>
					</BreadcrumbsFlexWrapper>
				</svelte:fragment>
			</Toolbar>

			{#if flowsList.length === 0}
				<div class="empty-state">
					<Text variant="body" size="l" class="text-center">
						{$format('label_no_flows_yet', { default: 'No flows yet' })}
					</Text>
					<Text variant="body" size="m" class="text-center mt-s">
						{$format('label_create_first_flow', {
							default: 'Create your first automation flow',
						})}
					</Text>
				</div>
			{:else}
				<CardGrid>
					{#each flowsList as flow (flow._id || flow.id)}
						{@const flowWithId = { ...flow, id: flow._id || flow.id }}
						<ListMenuCard
							title={flow._meta?.name || 'Unnamed Flow'}
							subtitle={flow._enabled
								? $format('label_enabled', { default: 'Enabled' })
								: $format('label_disabled', { default: 'Disabled' })}
							icon={flow._meta?.image || 'fa-diagram-project'}
							backgroundColor={listMenuCardBackground(flow._meta)}
							editMode={$edit}
							selected={($sidePanelStateCombined.type === 'flow' ||
								$sidePanelStateCombined.type === 'flows') &&
								String(flow._id || flow.id) === String($sidePanelStateCombined.id)}
							on:click={() => onCardClick(flow)}
						>
							<svelte:fragment slot="right">
								{#if $edit && !isMobile}
									<ActionsContainer orientation="vertical">
										<IconButton
											icon="fas fa-pen"
											slot="first"
											on:click={(e) => onCardEdit(e, flow)}
											aria-label={$format('aria_edit_scene', {
												default: 'Edit flow',
											})}
										/>
										<IconButton
											icon="fas fa-trash"
											variant="danger"
											slot="second"
											on:click={(e) => openDeleteModal(e, flow)}
											aria-label={$format('label_delete_flow', {
												default: 'Delete flow',
											})}
										/>
									</ActionsContainer>
								{:else}
									<ControlsScene item={flowWithId} variant="listMenuCard" />
								{/if}
							</svelte:fragment>
						</ListMenuCard>
					{/each}
				</CardGrid>
			{/if}
		</Stack>
	</Container>

	<Modal
		showModal={showDeleteModal}
		title={$format('flow_delete_confirm_title', { default: 'Delete flow?' })}
		on:close={closeDeleteModal}
	>
		<svelte:fragment slot="content">
			<Text variant="body" size="m">
				{$format('flow_delete_confirm_message', {
					default: 'Are you sure you want to delete "{name}"? This cannot be undone.',
					values: { name: flowToDelete?._meta?.name || 'Unnamed Flow' },
				})}
			</Text>
			<div class="modal-actions">
				<Button variant="secondary" on:click={closeDeleteModal}>
					{$format('label_cancel', { default: 'Cancel' })}
				</Button>
				<Button variant="danger" on:click={confirmDeleteFlow}>
					{$format('label_delete', { default: 'Delete' })}
				</Button>
			</div>
		</svelte:fragment>
	</Modal>

	<FlowSharingModal
		showModal={showImportModal}
		mode="import"
		on:close={() => (showImportModal = false)}
		on:import={handleFlowImport}
	/>
{/if}

<style>
	.flow-editor-route {
		display: flex;
		flex-direction: row;
		overflow: hidden;
		height: 100%;
		min-height: 0;
		isolation: isolate;
	}

	.flow-editor-main {
		min-width: 0;
		min-height: 0;
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	@media (max-width: 700px) {
		.flow-editor-route {
			flex-direction: column;
		}
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		gap: 1rem;
	}

	.modal-actions {
		display: flex;
		gap: 0.75rem;
		justify-content: flex-end;
		margin-top: 1.5rem;
	}
</style>
